import { ActionIcon, Box, Group, Paper, rem, SimpleGrid, Stack, Table, Text, Timeline, Tooltip } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { BuildingsIcon, TruckIcon, WarehouseIcon, XIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import { IInboundOrderItem } from "../../apis/inbound-order"
import { formatQuantity } from "../../utils/format-quantity"
import { ListState } from "../ui"
import { InboundExceptionsPanel } from "./InboundExceptionsPanel"
import { InboundActionsPanel } from "./InboundActionsPanel"
import { InboundStatusBadge, ItemConditionBadge, SourceTypeBadge } from "./atoms"
import { useExternalPartiesQuery, useInboundExceptionsQuery, useInboundOrderQuery } from "./hooks"
import { inboundItemLabel } from "./inbound-item-label"
import { PickupCompleteDialog } from "./PickupCompleteDialog"
import { PickupStartDialog } from "./PickupStartDialog"
import { RaiseExceptionDialog } from "./RaiseExceptionDialog"
import { ReceiveDialog } from "./ReceiveDialog"

type Props = Readonly<{
  inboundOrderId: string
  onClose?: () => void
}>

// PICKUP_COMPLETED is never written (contract §2 O134), so the flow has no such step.
const FLOW_STEPS = ["DRAFT", "APPROVED", "DISPATCHED_PICKUP", "IN_TRANSIT", "AT_DESTINATION", "RECEIVED"] as const

export function InboundOrderDetail({ inboundOrderId, onClose }: Props) {
  const { t } = useTranslation(["inbound", "common"])
  const { data: order, isLoading, error, mutate } = useInboundOrderQuery(inboundOrderId)
  const { data: exceptions } = useInboundExceptionsQuery(inboundOrderId)
  const { data: externalPartiesData } = useExternalPartiesQuery()

  const [pickupStartOpen, pickupStart] = useDisclosure(false)
  const [pickupCompleteOpen, pickupComplete] = useDisclosure(false)
  const [receiveOpen, receive] = useDisclosure(false)
  const [raiseOpen, raise] = useDisclosure(false)

  if (isLoading) return <ListState state="loading" columns={2} rows={6} />
  if (error || !order) {
    const status = (error as { statusCode?: number } | undefined)?.statusCode
    return (
      <Box p="md">
        <ListState state="error" description={t("inbound:detail.loadError")} errorCode={status} onRetry={() => mutate()} />
      </Box>
    )
  }

  const items: IInboundOrderItem[] = order.items ?? []
  const isCancelled = order.status === "CANCELLED"
  // A legacy PICKUP_COMPLETED row sits on the pickup step.
  const stepKey = order.status === "PICKUP_COMPLETED" ? "DISPATCHED_PICKUP" : order.status
  const activeStepIdx = isCancelled ? -1 : FLOW_STEPS.findIndex((s) => s === stepKey)
  const totalRequested = items.reduce((sum, it) => sum + (Number(it.requestedQuantity) || 0), 0)
  const totalReceived = items.reduce((sum, it) => sum + (Number(it.receivedQuantity) || 0), 0)

  const external = order.externalPartyId ? (externalPartiesData?.rows?.find((p) => p.id === order.externalPartyId) ?? null) : null
  const isExternal = order.sourceType === "EXTERNAL_PARTY"
  const sourcePrimary = isExternal ? (external?.name ?? "-") : (order.fromWarehouseName ?? "-")
  const sourceContact = isExternal ? ([order.externalPartyContactName, order.externalPartyContactPhone].filter(Boolean).join(" · ") || null) : null
  const sourceLocation = isExternal ? (order.externalPartyAddressOverride ?? external?.city ?? null) : null

  return (
    <Box>
      <Box pos="sticky" top={0} bg="var(--mantine-color-body)" style={{ zIndex: 5, borderBottom: "1px solid var(--mantine-color-gray-2)" }}>
        <Group justify="space-between" align="flex-start" p="md" wrap="nowrap">
          <Box miw={0} style={{ flex: 1 }}>
            <Text fz={14} fw={600} c="gray.8">
              {t("inbound:detail.inboundNumber")}
            </Text>
            <Text fz={24} fw={700} ff="monospace" lh={1.2} truncate>
              {order.inboundNumber ?? "-"}
            </Text>
            <Group gap="sm" mt={6} wrap="wrap">
              <SourceTypeBadge sourceType={order.sourceType} />
              <InboundStatusBadge status={order.status} />
              <Text fz={14} ff="monospace">
                {order.createdAt ? dayjs(order.createdAt).format("YYYY-MM-DD HH:mm") : "-"}
              </Text>
            </Group>
          </Box>
          {onClose && (
            <Tooltip label={t("inbound:detail.close")} position="left">
              <ActionIcon variant="light" color="gray" size="lg" onClick={onClose} aria-label={t("inbound:detail.close")}>
                <XIcon size={18} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        {!isCancelled && activeStepIdx >= 0 && (
          <Box px="md" pb="md">
            <Box pos="relative">
              <Box pos="absolute" top={rem(6)} left={rem(7)} right={rem(7)} bg="gray.3" h={2} />
              {activeStepIdx > 0 && (
                <Box
                  pos="absolute"
                  top={rem(6)}
                  left={rem(7)}
                  bg="dark.6"
                  h={2}
                  style={{ width: `calc((100% - ${rem(14)}) * ${activeStepIdx / (FLOW_STEPS.length - 1)})` }}
                />
              )}
              <Group justify="space-between" align="flex-start" gap={0} wrap="nowrap" pos="relative">
                {FLOW_STEPS.map((step, i) => {
                  const reached = i <= activeStepIdx
                  return (
                    <Box key={step} ta="center" miw={rem(56)} maw={rem(104)}>
                      <Box
                        mx="auto"
                        w={14}
                        h={14}
                        bg={reached ? "dark.6" : "gray.3"}
                        style={{
                          borderRadius: "50%",
                          border: "2px solid var(--mantine-color-body)",
                          boxShadow: i === activeStepIdx ? "0 0 0 4px color-mix(in srgb, var(--mantine-color-dark-6) 20%, transparent)" : undefined,
                        }}
                      />
                      <Text fz={14} mt={8} fw={i === activeStepIdx ? 700 : 500} c={reached ? "dark.7" : "gray.7"} lh={1.2}>
                        {t(`inbound:status.${step}`)}
                      </Text>
                    </Box>
                  )
                })}
              </Group>
            </Box>
          </Box>
        )}

        {isCancelled && (
          <Box px="md" pb="md">
            <Paper bg="gray.1" radius="md" p="sm">
              <Text fz={14} fw={700}>
                {t("inbound:detail.cancelled")}
              </Text>
              <Text fz={14}>
                {t("inbound:detail.cancelledReason")}: {order.cancelledReason || "-"}
              </Text>
              {order.cancelledAt && (
                <Text fz={14} c="gray.8" ff="monospace">
                  {dayjs(order.cancelledAt).format("YYYY-MM-DD HH:mm")}
                </Text>
              )}
            </Paper>
          </Box>
        )}
      </Box>

      <Stack gap="md" p="md">
        <SimpleGrid cols={3} spacing="xs">
          <SummaryStat label={t("inbound:detail.summaryItems")} value={items.length} />
          <SummaryStat label={t("inbound:detail.summaryRequested")} value={formatQuantity(totalRequested)} />
          <SummaryStat label={t("inbound:detail.summaryReceived")} value={formatQuantity(totalReceived)} />
        </SimpleGrid>

        <Paper withBorder radius="md" p="md">
          <Text fz={14} fw={700} tt="uppercase" c="gray.8" mb="sm">
            {t("inbound:detail.route")}
            <HelpIcon helpKey="order.inbound.section.route" variant="section" />
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <RouteCell
              icon={isExternal ? <BuildingsIcon size={18} /> : <WarehouseIcon size={18} />}
              label={isExternal ? t("inbound:detail.sourceExternal") : t("inbound:detail.sourceWarehouse")}
              primary={sourcePrimary}
              secondary={sourceContact}
              tertiary={sourceLocation}
            />
            <RouteCell icon={<TruckIcon size={18} />} label={t("inbound:detail.destination")} primary={order.toWarehouseName ?? "-"} />
          </SimpleGrid>
          {order.notes && (
            <Text fz={14} mt="sm">
              {t("inbound:detail.notes")}: {order.notes}
            </Text>
          )}
        </Paper>

        <InboundActionsPanel order={order} onPickupStart={pickupStart.open} onPickupComplete={pickupComplete.open} onReceive={receive.open} />

        <Paper withBorder radius="md" p="md">
          <Group justify="space-between" mb="sm">
            <Text fz={14} fw={700} tt="uppercase" c="gray.8">
              {t("inbound:detail.itemsTitle")}
              <HelpIcon helpKey="order.inbound.section.items" variant="section" />
            </Text>
            <Text fz={14}>{t("inbound:detail.itemsCount", { count: items.length })}</Text>
          </Group>
          {items.length === 0 ? (
            <Text fz={14}>{t("inbound:detail.noItems")}</Text>
          ) : (
            <Table.ScrollContainer minWidth={420}>
              <Table verticalSpacing="xs" horizontalSpacing="sm">
                <Table.Thead bg="gray.0">
                  <Table.Tr>
                    <Table.Th fz={14}>{t("inbound:detail.colItem")}</Table.Th>
                    <Table.Th fz={14} ta="right">
                      {t("inbound:detail.colRequested")}
                    </Table.Th>
                    <Table.Th fz={14} ta="right">
                      {t("inbound:detail.colDispatched")}
                    </Table.Th>
                    <Table.Th fz={14} ta="right">
                      {t("inbound:detail.colReceived")}
                    </Table.Th>
                    <Table.Th fz={14}>{t("inbound:detail.colCondition")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {items.map((item) => (
                    <Table.Tr key={item.id}>
                      <Table.Td>
                        <Text fz={14} fw={600}>
                          {inboundItemLabel(item, t)}
                        </Text>
                        <Text fz={14} c="gray.8">
                          {item.unit || "-"}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text fz={14} ff="monospace">
                          {formatQuantity(item.requestedQuantity)}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text fz={14} ff="monospace">
                          {formatQuantity(item.dispatchedQuantity)}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text fz={14} fw={700} ff="monospace">
                          {formatQuantity(item.receivedQuantity)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <ItemConditionBadge condition={item.condition} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Paper>

        <InboundExceptionsPanel inboundOrderId={inboundOrderId} exceptions={exceptions} onRaise={raise.open} />

        <Paper withBorder radius="md" p="md">
          <Text fz={14} fw={700} tt="uppercase" c="gray.8" mb="md">
            {t("inbound:detail.timelineTitle")}
            <HelpIcon helpKey="order.inbound.section.timeline" variant="section" />
          </Text>
          <Timeline active={order.updatedAt ? 1 : 0} bulletSize={14} lineWidth={2}>
            {order.createdAt && (
              <Timeline.Item title={<Text fz={14} fw={600}>{t("inbound:detail.created")}</Text>}>
                <Text fz={14} ff="monospace">
                  {dayjs(order.createdAt).format("YYYY-MM-DD HH:mm")}
                </Text>
              </Timeline.Item>
            )}
            {order.updatedAt && order.updatedAt !== order.createdAt && (
              <Timeline.Item title={<Text fz={14} fw={600}>{t("inbound:detail.updated")}</Text>}>
                <Text fz={14} ff="monospace">
                  {dayjs(order.updatedAt).format("YYYY-MM-DD HH:mm")}
                </Text>
              </Timeline.Item>
            )}
          </Timeline>
        </Paper>
      </Stack>

      <PickupStartDialog opened={pickupStartOpen} onClose={pickupStart.close} inboundOrderId={inboundOrderId} />
      <PickupCompleteDialog opened={pickupCompleteOpen} onClose={pickupComplete.close} inboundOrderId={inboundOrderId} items={items} />
      <ReceiveDialog opened={receiveOpen} onClose={receive.close} inboundOrderId={inboundOrderId} items={items} />
      <RaiseExceptionDialog opened={raiseOpen} onClose={raise.close} inboundOrderId={inboundOrderId} items={items} />
    </Box>
  )
}

function SummaryStat({ label, value }: Readonly<{ label: string; value: ReactNode }>) {
  return (
    <Paper withBorder radius="md" p="sm">
      <Text fz={14} c="gray.8">
        {label}
      </Text>
      <Text fz={24} fw={700} ff="monospace" lh={1.2}>
        {value}
      </Text>
    </Paper>
  )
}

function RouteCell({ icon, label, primary, secondary, tertiary }: Readonly<{ icon: ReactNode; label: string; primary: string; secondary?: string | null; tertiary?: string | null }>) {
  return (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      <Box c="gray.8" bg="gray.1" p="xs" style={{ borderRadius: "var(--mantine-radius-sm)", display: "flex", flexShrink: 0 }}>
        {icon}
      </Box>
      <Box miw={0} style={{ flex: 1 }}>
        <Text fz={14} fw={600} c="gray.8">
          {label}
        </Text>
        <Text fz={16} fw={700} lh={1.3}>
          {primary}
        </Text>
        {secondary && <Text fz={14}>{secondary}</Text>}
        {tertiary && (
          <Text fz={14} c="gray.8">
            {tertiary}
          </Text>
        )}
      </Box>
    </Group>
  )
}
